import { isNil } from 'lodash';
import { ReactNode, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef } from 'react';
import { getAnchorName } from '@shared/styles';
import { useBoolean, useDidPropChange } from '@shared/util/hooks';
import { useDisableGlobalHotkeys } from '@shared/util/hotkeys';
import {
  MenuAlignment,
  MenuContext,
  MenuContextValue,
  ParentMenuContext,
  RootMenuContext,
} from './MenuContext';
import { MenuKeyboardNavigation } from './MenuKeyboardNavigation';
import { restorePreviousElement, stashPreviousElement } from './menuPreviousActiveElement';

export interface MenuProps {
  children?: ReactNode;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  isOpenByDefault?: boolean;
  anchorName?: string;
  align?: MenuAlignment;
  closeOnSelect?: boolean;
  lightDismiss?: boolean;
}

export function MenuRoot({
  children,
  isOpen: isOpenProp,
  onOpenChange,
  isOpenByDefault = false,
  anchorName: anchorNameProp,
  align = 'right',
  closeOnSelect = true,
  lightDismiss = true,
}: MenuProps) {
  const rawId = useId();
  const anchorName = anchorNameProp ?? getAnchorName('menu', rawId);

  const rootMenu = useContext(RootMenuContext);
  const parentMenu = useContext(MenuContext);
  const isRootMenu = rootMenu === null;

  const [isOpen, open, close, toggle, , setValue] = useBoolean(isOpenProp ?? false);
  const previousElementRef = useRef<HTMLElement | null>(null);

  // Global hotkeys are disabled when a menu is open.
  useDisableGlobalHotkeys(isOpen);

  // Open after mount when isOpenByDefault is true. Using a ref + effect (rather than
  // initializing useState with isOpenByDefault) ensures the parent popover's showPopover()
  // has already run before the child tries to open — otherwise the browser's auto-dismiss
  // logic closes the child when the parent popover subsequently calls showPopover().
  const isOpenByDefaultRef = useRef(isOpenByDefault);
  useEffect(() => {
    if (isOpenByDefaultRef.current) {
      open();
    }
  }, [open]);

  useEffect(() => {
    if (!isNil(isOpenProp)) {
      setValue(isOpenProp);
    }
  }, [isOpenProp, setValue]);

  // Only call onOpenChange when isOpen actually changes (not on mount or when
  // the handler identity changes between renders).
  useDidPropChange(isOpen, () => onOpenChange?.(isOpen));

  // Hand focus back to whatever opened the menu once it closes, so keyboard
  // users don't land at the top of the document.
  useLayoutEffect(() => {
    if (isOpen) {
      stashPreviousElement(previousElementRef);
    } else {
      restorePreviousElement(previousElementRef);
    }
  }, [isOpen]);

  const menu = useMemo<MenuContextValue>(
    () => ({ anchorName, isOpen, open, close, toggle, closeOnSelect, align, lightDismiss }),
    [anchorName, isOpen, open, close, toggle, closeOnSelect, align, lightDismiss],
  );

  return (
    <RootMenuContext.Provider value={rootMenu ?? menu}>
      <ParentMenuContext.Provider value={parentMenu}>
        <MenuContext.Provider value={menu}>
          {/* Root only: the handler walks the topmost open menu itself, so a
              registration per nested submenu would just duplicate the work. */}
          {isRootMenu && <MenuKeyboardNavigation />}
          {children}
        </MenuContext.Provider>
      </ParentMenuContext.Provider>
    </RootMenuContext.Provider>
  );
}

export function SubmenuRoot(props: MenuProps) {
  return <MenuRoot {...props} />;
}
